import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Database, Lock, UserCheck, Mail, Globe } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const content = isRTL
    ? {
        title: 'سياسة الخصوصية',
        subtitle: 'نظام لجنة النظافة - خدمات اللجنة',
        lastUpdated: 'آخر تحديث: مايو 2026',
        intro:
          'يجمع هذا التطبيق فقط المعلومات الضرورية لمصادقة المستخدم وإدارة خدمات اللجنة.',
        sections: [
          {
            icon: <Database className="w-6 h-6 text-brand-600" />,
            title: 'البيانات التي نجمعها',
            items: [
              'اسم المستخدم وكلمة المرور لتسجيل الدخول',
              'بيانات العمل المتعلقة بخدمات اللجنة',
            ],
          },
          {
            icon: <Lock className="w-6 h-6 text-brand-600" />,
            title: 'كيف نحمي بياناتك',
            items: [
              'تُخزَّن البيانات بشكل آمن ولا تُشارَك مع أطراف ثالثة',
              'تُستخدَم البيانات فقط للعمليات الداخلية للجنة',
            ],
          },
          {
            icon: <UserCheck className="w-6 h-6 text-brand-600" />,
            title: 'حقوقك',
            items: [
              'يمكنك طلب الاطلاع على بياناتك الشخصية أو تصحيحها في أي وقت',
              'تواصل معنا لأي استفسار يتعلق ببياناتك',
            ],
          },
        ],
        contactTitle: 'تواصل معنا',
        contactText: 'لأي استفسار يتعلق بسياسة الخصوصية يرجى التواصل على:',
        contactEmail: 'drisha.sahby@gmail.com',
      }
    : {
        title: 'Privacy Policy',
        subtitle: 'Committee Services System',
        lastUpdated: 'Last updated: May 2026',
        intro:
          'This app collects only the information necessary for user authentication and committee services management.',
        sections: [
          {
            icon: <Database className="w-6 h-6 text-brand-600" />,
            title: 'Data Collected',
            items: [
              'Username and password for login',
              'Committee-related work data',
            ],
          },
          {
            icon: <Lock className="w-6 h-6 text-brand-600" />,
            title: 'How We Protect Your Data',
            items: [
              'Data is stored securely and is not shared with third parties',
              'Data is used only for internal committee operations',
            ],
          },
          {
            icon: <UserCheck className="w-6 h-6 text-brand-600" />,
            title: 'Your Rights',
            items: [
              'You may request access to or correction of your personal data at any time',
              'Contact us for any inquiries regarding your data',
            ],
          },
        ],
        contactTitle: 'Contact Us',
        contactText:
          'For any inquiries regarding this Privacy Policy, please reach out at:',
        contactEmail: 'drisha.sahby@gmail.com',
      };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-emerald-50 relative overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
        <div
          className="absolute top-20 right-20 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"
          style={{ animationDelay: '2s' }}
        />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzE2YTM0YSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40" />
      </div>

      {/* Language Toggle */}
      <div className={`absolute top-6 z-20 ${isRTL ? 'left-6' : 'right-6'}`}>
        <button
          onClick={() => {
            const next = isRTL ? 'en' : 'ar';
            i18n.changeLanguage(next);
            localStorage.setItem('language', next);
            document.documentElement.dir = next === 'ar' ? 'rtl' : 'ltr';
            document.documentElement.lang = next;
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm border-2 border-brand-200 hover:border-brand-400 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <Globe className="w-5 h-5 text-brand-700" />
          <span className="font-semibold text-gray-700">
            {isRTL ? 'English' : 'العربية'}
          </span>
        </button>
      </div>

      {/* Main Card */}
      <div className="relative z-10 flex justify-center px-4 py-16">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 px-8 py-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-800/20 rounded-full -ml-12 -mb-12" />
              <div className="relative text-center">
                <div className="mx-auto w-20 h-20 rounded-3xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl mb-4 border-4 border-white/30">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-black text-white mb-2">
                  {content.title}
                </h1>
                <p className="text-brand-50 text-lg font-medium">
                  {content.subtitle}
                </p>
                <p className="text-brand-200 text-sm mt-1">
                  {content.lastUpdated}
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="p-8 space-y-6">
              {/* Intro */}
              <p className="text-gray-700 text-base leading-relaxed font-medium">
                {content.intro}
              </p>

              {/* Sections */}
              {content.sections.map((section, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-r from-brand-50 to-emerald-50 rounded-2xl border border-brand-100 p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white shadow flex items-center justify-center flex-shrink-0">
                      {section.icon}
                    </div>
                    <h2 className="text-lg font-bold text-gray-800">
                      {section.title}
                    </h2>
                  </div>
                  <ul className="space-y-2">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Contact */}
              <div className="bg-white border-2 border-brand-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-brand-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-800">
                    {content.contactTitle}
                  </h2>
                </div>
                <p className="text-gray-600 text-sm mb-2">{content.contactText}</p>
                <a
                  href={`mailto:${content.contactEmail}`}
                  className="text-brand-600 hover:text-brand-700 font-semibold text-sm transition-colors"
                >
                  {content.contactEmail}
                </a>
              </div>
            </div>

            {/* Bottom Accent */}
            <div className="h-2 bg-gradient-to-r from-brand-500 via-brand-600 to-emerald-600" />
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 font-medium mt-6">
            © 2026{' '}
            {isRTL ? 'نظام لجنة النظافة' : 'Cleaning Committee System'} - v1.0.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
